import { calculateFeasibility } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { FeasibilityResult } from '../../types';
import type { FeasibilityInput } from './feasibilityInput';

export interface FeasibilityState {
  /** the server's latest answer for the settled input, or null when there is none (yet) */
  data: FeasibilityResult | null;
  loading: boolean;
  error: unknown;
  /** the user is still typing: `data` belongs to slightly older values */
  updating: boolean;
}

/**
 * The server's feasibility answer for the wizard's current, possibly unsaved, values (POST /feasibility).
 * Calls are debounced while the user types, a newer call cancels an older one, and a null input (no country yet)
 * makes no call at all. Failures are returned as `error`, never thrown, so the screens can say "not available".
 */
export function useFeasibility(input: FeasibilityInput | null, delayMs = 500): FeasibilityState {
  const key = input ? JSON.stringify(input) : '';
  const settled = useDebouncedValue(key, delayMs);
  const result = useAsyncData<FeasibilityResult>(
    (signal) => calculateFeasibility(JSON.parse(settled), { signal }),
    [settled],
    { enabled: settled !== '' }
  );
  return {
    data: settled === '' ? null : result.data,
    loading: settled !== '' && result.loading,
    error: settled === '' ? null : result.error,
    updating: key !== settled,
  };
}
